import { SyntheticEvent, useEffect, useState } from "react";

// ** MUI Imports
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CardContent from "@mui/material/CardContent";
import InputAdornment from "@mui/material/InputAdornment";

// ** Custom Component Import

// ** Icon Imports

// ** Third Party Imports
import axios from "axios";

// ** Config
import _ from "lodash";
import { appColor, EndPoint } from "../../common/constants";
import { serviceAPIMapping } from "../../config/service-api-mapping";
import { Box, styled, Tab, TextField } from "@mui/material";
import { Icon } from "@iconify/react";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router";
import swal from "sweetalert";
import withUser from "../../redux/HOC/withUser";
import { TabContext, TabPanel } from "@mui/lab";
import MuiTabList, { TabListProps } from "@mui/lab/TabList";
import { getDivHeight } from "../../common/commonFunctions";

interface State {
  showNewPassword: boolean;
  showConfirmNewPassword: boolean;
  showOldPassword: boolean;
}

const defaultValues = {
  newPassword: "",
  confirmNewPassword: "",
  oldPassword: "",
};

const schema = yup.object().shape({
  newPassword: yup
    .string()
    .min(8)
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
      "Must contain 8 characters, 1 uppercase, 1 lowercase, 1 number and 1 special case character"
    )
    .required("New Password is required"),
  confirmNewPassword: yup
    .string()
    .required("Confirm New Password is required")
    .oneOf([yup.ref("newPassword")], "Passwords must match"),
  oldPassword: yup.string().required("Old Password is required"),
});

const TabList = styled(MuiTabList)<TabListProps>(({ theme }) => ({
  borderBottom: "0 !important",
  "&, & .MuiTabs-scroller": {
    boxSizing: "content-box",
    padding: theme.spacing(1.25, 1.25, 2),
    margin: `${theme.spacing(-1.25, -1.25, -2)} !important`,
  },
  "& .MuiTabs-indicator": {
    display: "none",
  },
  "& .Mui-selected": {
    boxShadow: theme.shadows[2],
    backgroundColor: `${appColor}`,
    color: `${theme.palette.common.white} !important`,
  },
  "& .MuiTab-root": {
    lineHeight: 1,
    borderRadius: theme.shape.borderRadius,
    "&:hover": {
      color: `${appColor}`,
    },
  },
}));

const ChangePassword = ({ user }: any) => {
  const [values, setValues] = useState<State>({
    showNewPassword: false,
    showConfirmNewPassword: false,
    showOldPassword: false,
  });
  const token = window.localStorage.getItem("refreshtoken");
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState("User Details");
  const [currentActiveAccountCode, setActiveAccountCode] = useState<any>(null);
  const [currentActiveClient, setActiveClient] = useState<any>(null);

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues, resolver: yupResolver(schema) });

  const handleClickShowNewPassword = () => {
    setValues({ ...values, showNewPassword: !values.showNewPassword });
  };

  const handleClickOldPassword = () => {
    setValues({ ...values, showOldPassword: !values.showOldPassword });
  };

  const handleClickShowConfirmNewPassword = () => {
    setValues({
      ...values,
      showConfirmNewPassword: !values.showConfirmNewPassword,
    });
  };

  const onPasswordFormSubmit = (data: any) => {
    if (
      data.newPassword &&
      !_.isEmpty(data.newPassword) &&
      _.isEqual(data.confirmNewPassword, data.newPassword)
    ) {
      const request = {
        refreshToken: token,
        newPassword: data?.newPassword,
        oldPassword: data?.oldPassword,
      };

      axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

      axios
        .post(
          serviceAPIMapping["AUTH-SERVICE"].apiRouteURL +
            EndPoint.authBase +
            "changePassword",
          request
        )
        .then(async (response: any) => {
          if (response?.status === 200) {
            swal({
              icon: "success",
              text: `Password changed successfully`,
              dangerMode: false,
              closeOnClickOutside: false,
            }).then(() => {
              navigate("/signin");
            });
          } else if (response?.status === 400) {
            swal({
              icon: "error",
              text: `${response?.data?.message}`,
              dangerMode: false,
              closeOnClickOutside: false,
            });
          }
        })
        .catch((err) => {
          if (err?.response?.status === 400) {
            swal({
              icon: "error",
              text: `${err}`,
              dangerMode: false,
              closeOnClickOutside: false,
            });
          } else {
            swal({
              icon: "error",
              text: `${err}`,
              dangerMode: false,
              closeOnClickOutside: false,
            });
          }
        });
    }
  };

  const handleTabChange = (event: SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
    reset();
  };

  useEffect(() => {
    let activeAccountCode = window.localStorage.getItem("activeAccountCode");
    let activeClient = window.localStorage.getItem("activeClient");

    if (activeAccountCode) {
      setActiveAccountCode(activeAccountCode);
    }
    if (activeClient) {
      setActiveClient(activeClient);
    }
  }, []);

  return (
    <>
      <div
        id="page-title"
        className="col mx-auto p-2 d-flex align-items-center"
        style={{ height: "1.8em" }}
      >
        User Profile
      </div>
      <Card
        className="justify-content-start m-1 p-0"
        style={{ height: getDivHeight("page-title", 18) }}
      >
        <CardContent className="h-100 justify-content-start overflow-auto p-0">
          <TabContext value={selectedTab}>
            <div className="col-xl-4 col-lg-4 col-sm-12 ">
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList
                  variant="scrollable"
                  id="locate-tab-list"
                  scrollButtons="auto"
                  onChange={handleTabChange}
                  aria-label="locate-tab-list"
                  indicatorColor="primary"
                  textColor="inherit"
                >
                  <Tab
                    value="User Details"
                    label={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          ...{ "& svg": { mr: 2 } },
                        }}
                      >
                        <Icon
                          className="me-2"
                          fontSize="1.25rem"
                          icon="tabler:users"
                        />
                        {"User Details"}
                      </div>
                    }
                  />
                  <Tab
                    value="Change Password"
                    label={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          ...{ "& svg": { mr: 4 } },
                        }}
                      >
                        <Icon
                          className="me-2"
                          fontSize="1.25rem"
                          icon="mdi:password-outline"
                        />
                        {"Change Password"}
                      </div>
                    }
                  />
                </TabList>
              </Box>
            </div>

            <TabPanel
              className="w-100 p-1 locate-panel my-2 p-2 ps-3"
              value="User Details"
            >
              <div className="col-xl-4 col-lg-4 col-sm-12 mt-2">
                <form>
                  <fieldset>
                    <legend>User Details</legend>
                    {/* <div className="d-flex justify-content-start align-items-center">
                  <Typography variant="h6">User Details</Typography>
                </div> */}
                    <div className="my-3 row">
                      <label className="col d-flex align-items-center">
                        User Name
                      </label>
                      <TextField
                        className="col p-0"
                        value={user?.userName}
                        disabled
                        fullWidth
                        sx={{ borderRadius: 4, padding: 0 }}
                        size="small"
                      ></TextField>
                    </div>
                    <div className="mt-3 row">
                      <label className="col d-flex align-items-center">
                        First Name
                      </label>
                      <TextField
                        className="col"
                        value={user?.firstName}
                        disabled
                        fullWidth
                        sx={{ borderRadius: 4 }}
                        size="small"
                      ></TextField>
                    </div>
                    <div className="my-3 row">
                      <label className="col d-flex align-items-center">
                        Last Name
                      </label>
                      <TextField
                        value={user?.lastName}
                        disabled
                        fullWidth
                        sx={{ borderRadius: 4 }}
                        className="col"
                        size="small"
                      ></TextField>
                    </div>
                    <div className="my-3 row">
                      <label className="col d-flex align-items-center">
                        Full Name
                      </label>
                      <TextField
                        value={user?.fullName}
                        disabled
                        fullWidth
                        sx={{ borderRadius: 4 }}
                        className="col"
                        size="small"
                      ></TextField>
                    </div>
                    <div className="my-3 row">
                      <label className="col d-flex align-items-center">
                        Email
                      </label>
                      <TextField
                        className="col"
                        value={user?.email}
                        disabled
                        fullWidth
                        sx={{ borderRadius: 4 }}
                        size="small"
                      ></TextField>
                    </div>
                    <div className="my-3 row">
                      <label className="col d-flex align-items-center">
                        Current Client
                      </label>
                      <TextField
                        className="col"
                        value={currentActiveClient}
                        disabled
                        fullWidth
                        sx={{ borderRadius: 4 }}
                        size="small"
                      ></TextField>
                    </div>
                    <div className="my-3 row">
                      <label className="col d-flex align-items-center">
                        Current Account
                      </label>
                      <TextField
                        className="col"
                        value={currentActiveAccountCode}
                        disabled
                        fullWidth
                        sx={{ borderRadius: 4 }}
                        size="small"
                      ></TextField>
                    </div>
                    <div className="my-3 row">
                      
                      <Button
                          variant="contained"
                          style={{
                            backgroundColor: '',
                            color: "white",
                          }}
                          type="submit"
                          sx={{ mr: 4 }}
                          className="col"
                          disabled
                        >
                          Save
                        </Button>
                        <Button
                          type="reset"
                          style={{ borderColor: appColor, color: appColor }}
                          variant="outlined"
                          className="col cursor-banned"
                          disabled
                        >
                          Reset
                        </Button>
                    </div>
                  </fieldset>
                </form>
              </div>
            </TabPanel>
            <TabPanel
              className="w-100 p-2 locate-panel my-2 ps-3"
              value="Change Password"
            >
              <form
                onSubmit={handleSubmit(onPasswordFormSubmit)}
                autoComplete="off"
              >
                <div className="d-flex justify-content-start my-2">
                  <Typography variant="h6">Change Password</Typography>
                </div>
                <div className="row d-flex justify-content-start col-xl-4 col-lg-4 col-sm-12 mt-2">
                  <div className="my-1">
                    <Controller
                      name="oldPassword"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { value, onChange } }: any) => (
                        <TextField
                          fullWidth
                          value={value}
                          onChange={onChange}
                          label="Old Password"
                          id="input-old-password"
                          placeholder="············"
                          error={Boolean(errors.oldPassword)}
                          type={values.showOldPassword ? "text" : "password"}
                          autoComplete="off"
                          {...(errors.oldPassword && {
                            helperText: errors.oldPassword.message,
                          })}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  edge="end"
                                  onClick={handleClickOldPassword}
                                  onMouseDown={(e) => e.preventDefault()}
                                >
                                  <Icon
                                    fontSize="1.25rem"
                                    icon={
                                      values.showOldPassword
                                        ? "tabler:eye"
                                        : "tabler:eye-off"
                                    }
                                  />
                                </IconButton>
                              </InputAdornment>
                            ),
                            autoComplete: "new-password",
                          }}
                        />
                      )}
                    />
                  </div>
                  <div className="my-1">
                    <Controller
                      name="newPassword"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { value, onChange } }: any) => (
                        <TextField
                          fullWidth
                          value={value}
                          onChange={onChange}
                          label="New Password"
                          id="input-new-password"
                          placeholder="············"
                          error={Boolean(errors.newPassword)}
                          type={values.showNewPassword ? "text" : "password"}
                          autoComplete="off"
                          {...(errors.newPassword && {
                            helperText: errors.newPassword.message,
                          })}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  edge="end"
                                  onClick={handleClickShowNewPassword}
                                  onMouseDown={(e) => e.preventDefault()}
                                >
                                  <Icon
                                    fontSize="1.25rem"
                                    icon={
                                      values.showNewPassword
                                        ? "tabler:eye"
                                        : "tabler:eye-off"
                                    }
                                  />
                                </IconButton>
                              </InputAdornment>
                            ),
                            autoComplete: "new-password",
                          }}
                        />
                      )}
                    />
                  </div>
                  <div className="my-1">
                    <Controller
                      name="confirmNewPassword"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { value, onChange } }: any) => (
                        <TextField
                          fullWidth
                          value={value}
                          onChange={onChange}
                          placeholder="············"
                          label="Confirm New Password"
                          id="input-confirm-new-password"
                          error={Boolean(errors.confirmNewPassword)}
                          type={
                            values.showConfirmNewPassword ? "text" : "password"
                          }
                          {...(errors.confirmNewPassword && {
                            helperText: errors.confirmNewPassword.message,
                          })}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  edge="end"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={handleClickShowConfirmNewPassword}
                                >
                                  <Icon
                                    fontSize="1.25rem"
                                    icon={
                                      values.showConfirmNewPassword
                                        ? "tabler:eye"
                                        : "tabler:eye-off"
                                    }
                                  />
                                </IconButton>
                              </InputAdornment>
                            ),
                            autoComplete: "new-password",
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="d-flex justify-content-start pb-0 mt-4 mb-2">
                    <Typography variant="h6" className="me-4">
                      Password Requirements:
                    </Typography>
                    <div className="d-flex justify-content-start py-0 ms-2">
                      <ul>
                        <li>
                          Minimum 8 characters long - the more, the better
                        </li>
                        <li>
                          At least one lowercase & one uppercase character
                        </li>
                        <li>
                          At least one number, symbol, or whitespace character
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="d-flex justify-content-start mt-4">
                    <Button
                      variant="contained"
                      style={{
                        backgroundColor: appColor,
                        color: "white",
                      }}
                      type="submit"
                      sx={{ mr: 4 }}
                    >
                      Save
                    </Button>
                    <Button
                      type="reset"
                      style={{ borderColor: appColor, color: appColor }}
                      variant="outlined"
                      onClick={() => reset()}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </form>
            </TabPanel>
          </TabContext>
        </CardContent>
      </Card>
    </>
  );
};

export default withUser(ChangePassword);
